import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/cn";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    // NB: a plain dim, NO ``backdrop-blur``. A backdrop-filter over the whole
    // viewport forces the browser to re-rasterize the blurred region whenever
    // content on top repaints — a dialog that hosts a TRANSFORMING surface
    // (the sub-workflow drill-in's ReactFlow canvas) then flickers the whole
    // window on every zoom/pan. A slightly stronger dim reads the same.
    className={cn("fixed inset-0 z-40 bg-black/55", className)}
    {...props}
  />
));
DialogOverlay.displayName = "DialogOverlay";

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Mobile (<sm): full-screen sheet anchored to the viewport
        // edges. We use ``h-dvh`` (dynamic viewport height) so iOS
        // Safari's collapsing address bar doesn't push the sticky
        // footer below the visible area — the old ``95vh`` measured
        // against the *expanded* viewport and consistently chopped
        // the buttons off.
        "fixed inset-0 z-50 flex flex-col overflow-hidden bg-elevated",
        "h-dvh max-h-dvh w-screen max-w-none",
        // Desktop (sm+): floating centered panel. Restores rounded
        // corners + border + drop shadow + max-width. To override the
        // width, a caller MUST use the ``sm:`` variant (e.g.
        // ``sm:max-w-4xl``) — a BARE ``max-w-4xl`` loses to this
        // ``sm:max-w-lg`` at sm+ (the responsive variant wins in the
        // generated CSS regardless of class order). Existing wide
        // dialogs all pass ``sm:max-w-*``.
        "sm:fixed sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2",
        "sm:h-auto sm:w-full sm:max-w-lg sm:max-h-[90vh]",
        "sm:rounded-lg sm:border sm:border-border sm:shadow-xl",
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-2 top-2 p-1 hover:bg-muted">
        <X className="h-4 w-4" strokeWidth={1.5} />
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
DialogContent.displayName = "DialogContent";

export const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  // Bumped from h-10 → h-12 and px-4 → px-6: matches the new dialog
  // padding rhythm and gives the title breathing room.
  <div
    className={cn(
      "flex h-12 items-center border-b border-border/60 px-6 shrink-0",
      className,
    )}
    {...props}
  />
);

export const DialogTitle = DialogPrimitive.Title;
export const DialogDescription = DialogPrimitive.Description;

export const DialogBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  // Body is the only scrollable region — ``flex-1 min-h-0`` is what
  // forces it to share remaining space with the sticky header/footer
  // and clip its own overflow. Without ``min-h-0`` the body would
  // refuse to shrink below its content height, pushing the footer
  // below the viewport.
  <div
    className={cn("flex-1 min-h-0 overflow-auto p-6 space-y-4", className)}
    {...props}
  />
);

export const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  // Was h-10 — buttons looked squeezed. h-14 (56px) gives the action
  // row the visual weight a primary CTA deserves and leaves comfy
  // vertical padding on both sides of the buttons.
  <div
    className={cn(
      "flex flex-wrap items-center justify-end gap-2 border-t border-border/60 px-6 py-3 sm:h-14 sm:py-0 shrink-0",
      className,
    )}
    {...props}
  />
);
